import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, uploadSourceChunked, uploadEcoChunked } from "@/lib/api";
import { ExamFactsFields, emptyExamFacts, examFactErrors, examFactsPayload } from "./panel/ExamFacts";
import { TitlePreview } from "./panel/TitlePreview";

const input = "w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-sky-400";

export function EcoTree({ domains }) {
  return (
    <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 max-h-80 overflow-y-auto" data-testid="eco-tree">
      {domains.map((d, i) => (
        <div key={i} className="p-3">
          <p className="text-sm font-bold text-zinc-800">
            {d.name}{d.weight != null && <span className="ml-2 text-xs font-mono font-normal text-zinc-400">{d.weight}%</span>}
          </p>
          <ul className="mt-1 space-y-0.5">
            {d.objectives.map((o) => (
              <li key={o.id} className="text-xs text-zinc-600"><span className="font-mono text-zinc-400 mr-1">{o.id}</span>{o.title}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function EcoUploadPanel({ onExtracted }) {
  const [busy, setBusy] = useState(null); // number = upload %, string = stage text
  const [url, setUrl] = useState("");

  const poll = async (jobId) => {
    for (;;) {
      await new Promise((r) => setTimeout(r, 1500));
      const { data: j } = await api.get(`/admin/eco/jobs/${jobId}`);
      if (j.status === "done") return j.result;
      if (j.status === "failed") throw new Error(j.error || "ECO extraction failed");
      setBusy(j.detail || "Working…");
    }
  };
  const finish = async (jobId) => {
    setBusy("Reading document…");
    const result = await poll(jobId);
    onExtracted(result);
    toast.success(`${result.domains.length} domains, ${result.objective_count} objectives found`);
  };
  const fromFile = async (f) => {
    setBusy(0);
    try {
      const { eco_job_id } = await uploadEcoChunked(f, setBusy);
      await finish(eco_job_id);
    } catch (e) { toast.error(e?.message?.includes("extraction") || e?.message?.includes("received") ? e.message : errMsg(e)); }
    setBusy(null);
  };
  const fromUrl = async () => {
    setBusy("Fetching…");
    try {
      const { data } = await api.post("/admin/eco/extract-url", { url });
      await finish(data.eco_job_id);
    } catch (e) { toast.error(e?.message?.includes("extraction") || e?.message?.includes("received") ? e.message : errMsg(e)); }
    setBusy(null);
  };

  return (
    <div className="space-y-3">
      <label className="block border-2 border-dashed border-sky-300 bg-sky-50/40 rounded-md p-8 text-center cursor-pointer hover:border-sky-500 transition-colors duration-150">
        <input data-testid="eco-file-input" type="file" accept=".pdf,.docx,.txt,.md" className="hidden" disabled={busy !== null}
               onChange={(e) => { const f = e.target.files?.[0]; if (f) fromFile(f); e.target.value = ""; }} />
        {busy !== null ? (
          <span className="text-sm font-semibold text-sky-700" data-testid="eco-upload-progress">
            {typeof busy === "number" ? `Uploading… ${busy}%` : busy}
          </span>
        ) : (
          <>
            <span className="block text-sm font-semibold text-zinc-800">Upload the vendor's ECO document</span>
            <span className="block text-xs text-zinc-500 mt-1">PDF primary · DOCX · TXT · MD — the machine extracts the objectives, you just confirm</span>
          </>
        )}
      </label>
      <div className="flex items-center gap-2">
        <input data-testid="eco-url-input" className={input} placeholder="…or paste a URL to the vendor's ECO (PDF or page)"
               value={url} onChange={(e) => setUrl(e.target.value)} />
        <button data-testid="eco-url-btn" onClick={fromUrl} disabled={busy !== null || !url.trim()}
                className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-700 transition-colors duration-150 disabled:opacity-50 whitespace-nowrap">
          {busy !== null ? "Working…" : "Fetch & extract"}
        </button>
      </div>
    </div>
  );
}

export function CoveragePanel({ certId, refreshKey }) {
  const [cov, setCov] = useState(null);
  useEffect(() => {
    api.get(`/admin/certs/${certId}/coverage`).then((r) => setCov(r.data)).catch(() => {});
  }, [certId, refreshKey]);
  if (!cov) return null;
  const chip = { covered: "bg-emerald-100 text-emerald-700", thin: "bg-amber-100 text-amber-700", missing: "bg-red-100 text-red-700" };
  return (
    <div className="border border-zinc-200 rounded-md p-4 space-y-2" data-testid="coverage-panel">
      <p className="text-sm font-bold">Source coverage vs. ECO
        <span className="ml-2 text-xs font-normal text-zinc-400">— catch gaps before spending a generation run (ECO source excluded)</span></p>
      <p className="text-xs font-mono" data-testid="coverage-summary">
        <span className="text-emerald-700">{cov.summary.covered} covered</span> · <span className="text-amber-700">{cov.summary.thin} thin</span> · <span className="text-red-700">{cov.summary.missing} missing</span> across {cov.support_sources} support source{cov.support_sources === 1 ? "" : "s"}
      </p>
      <div className="max-h-64 overflow-y-auto divide-y divide-zinc-100">
        {cov.domains.map((d, i) => (
          <div key={i} className="py-2">
            <p className="text-xs font-bold text-zinc-700">{d.name}</p>
            {d.objectives.map((o) => (
              <div key={o.id} className="flex items-center gap-2 mt-1">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${chip[o.status]}`} data-testid={`coverage-${o.id}`}>{o.status}</span>
                <span className="text-xs text-zinc-600"><span className="font-mono text-zinc-400 mr-1">{o.id}</span>{o.title}</span>
                <span className="text-[10px] font-mono text-zinc-400">{o.hits} hit{o.hits === 1 ? "" : "s"}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SetupCheckPanel({ certId, refreshKey, accept, setAccept }) {
  const [check, setCheck] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/admin/certs/${certId}/setup-check`)
      .then(({ data }) => { if (live) setCheck(data); })
      .catch(() => {});
    return () => { live = false; };
  }, [certId, refreshKey]);
  if (!check) return null;
  const amber = check.rows.filter((r) => r.level === "single_source");
  const accepted = check.rows.filter((r) => r.level === "accepted");
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-2" data-testid="wizard-setup-check">
      <p className="text-xs font-bold text-zinc-700">Pre-flight: source support per objective</p>
      <p className="text-xs text-zinc-600 leading-relaxed" data-testid="setup-check-plain">{check.plain}</p>
      {check.errors.map((e, i) => (
        <p key={i} data-testid={`setup-check-error-${i}`} className="text-[11px] font-semibold text-red-700">✗ {e}</p>
      ))}
      {amber.length > 0 && (
        <div className="space-y-0.5" data-testid="setup-check-single-source">
          {amber.map((r) => (
            <p key={r.objective_id} className="text-[11px] text-amber-700">
              ⚠ {r.objective_id} {r.title} — only {r.sources ?? 1} source; add another or accept the risk
            </p>
          ))}
          <label className="flex items-center gap-2 text-xs text-zinc-700 pt-1" data-testid="setup-check-accept-toggle">
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} />
            Accept single-source risk for these objectives — the machine finishes the run and holds their flags for review
          </label>
        </div>
      )}
      {accepted.length > 0 && (
        <p className="text-[11px] text-zinc-500" data-testid="setup-check-accepted">
          ✓ risk already accepted: {accepted.map((r) => r.objective_id).join(", ")}
        </p>
      )}
      {check.errors.length === 0 && amber.length === 0 && accepted.length === 0 && (
        <p className="text-[11px] font-semibold text-emerald-700" data-testid="setup-check-green">✓ every objective has multi-source support</p>
      )}
    </div>
  );
}

function EcoLibraryOffer({ vendor, examCode, onUse }) {
  const [match, setMatch] = useState(null);
  useEffect(() => {
    if (!vendor || !examCode) return;
    api.get(`/admin/eco-library/match?vendor=${encodeURIComponent(vendor)}&exam_code=${encodeURIComponent(examCode)}`)
      .then((r) => setMatch(r.data.found ? r.data.entry : null)).catch(() => {});
  }, [vendor, examCode]);
  if (!match) return null;
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 flex items-center justify-between gap-3" data-testid="eco-library-offer">
      <p className="text-sm text-emerald-800">
        <b>Stored ECO found</b> — {match.vendor} / {match.exam_code}, {match.objective_count} objectives
        (extracted {new Date(match.extracted_at).toLocaleDateString()} from {match.source_filename}).
        Reuse it instantly, or upload below to re-extract a new ECO version.
      </p>
      <button data-testid="eco-library-use-btn" onClick={() => onUse(match)}
              className="shrink-0 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors duration-150">
        Use stored ECO
      </button>
    </div>
  );
}

function SourceLibraryPanel({ certId, onAttached }) {
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    api.get("/admin/source-library").then((r) => setEntries(r.data.entries)).catch(() => {});
  }, []);
  if (!entries.length) return null;
  const attach = async (e) => {
    setBusy(e.id);
    try {
      const { data } = await api.post(`/admin/certs/${certId}/sources/attach`, { library_id: e.id });
      toast.success(`${data.filename} attached — no re-upload needed`);
      onAttached(data);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(null);
  };
  return (
    <div className="rounded-md border border-zinc-200 p-3 space-y-1.5" data-testid="source-library-panel">
      <p className="text-xs font-bold text-zinc-600">Reuse an already-uploaded source</p>
      {entries.slice(0, 8).map((e) => (
        <div key={e.id} className="flex items-center justify-between gap-2 text-xs text-zinc-600">
          <span className="truncate">{e.filename} <span className="text-zinc-400">({(e.chars / 1000).toFixed(0)}k chars · {e.kind})</span></span>
          <button data-testid={`source-library-attach-${e.id}`} disabled={busy === e.id} onClick={() => attach(e)}
                  className="shrink-0 px-2 py-1 rounded border border-sky-300 text-sky-700 font-semibold hover:bg-sky-50 disabled:opacity-50">
            Attach
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Wizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [eco, setEco] = useState(null);
  const [ecoJson, setEcoJson] = useState("");
  const [det, setDet] = useState({ cert_name: "", keyword: "", min_monthly_searches: 2000, max_competition: 0.85, override: false });
  const [verdict, setVerdict] = useState(null);
  const [cfg, setCfg] = useState({ name: "", slug: "", vendor: "", exam_code: "", short_name: "", style_profile: "comptia_direct", version_tag: "v1.0", price_usd: 99, num_paid_exams: 10, questions_per_exam: 60, free_count: 50, udemy_exams: 2, udemy_questions: 100, duration_min: 90, video_questions: 150, video_episode_size: 15, youtube: true, meta: true, udemy: true, youtube_channel: "default" });
  const [ytChannels, setYtChannels] = useState(["default"]);
  useEffect(() => { api.get("/admin/config/youtube-channels").then((r) => setYtChannels(r.data.channels)).catch(() => {}); }, []);
  const [cert, setCert] = useState(null);
  const [source, setSource] = useState({ filename: "", kind: "study_guide", text: "" });
  const [sources, setSources] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [covKey, setCovKey] = useState(0);

  const runDetermination = async () => {
    try {
      const { data } = await api.post("/admin/determination", det);
      setVerdict(data);
      if (data.verdict === "go") { setCfg((c) => ({ ...c, name: det.cert_name, slug: det.cert_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })); }
    } catch (e) { toast.error(errMsg(e)); }
  };

  const [facts, setFacts] = useState(emptyExamFacts());
  const factErrors = examFactErrors(facts);
  const videoErrors = (() => {
    const vq = +cfg.video_questions, es = +cfg.video_episode_size;
    const errs = [];
    if (!(vq > 0)) errs.push("Video pool (questions) must be greater than 0 — every collection carries a video pool");
    if (!(es > 0)) errs.push("Questions per video episode must be greater than 0");
    if (vq > 0 && es > vq) errs.push(`Questions per video episode (${es}) cannot exceed the video pool (${vq})`);
    return errs;
  })();
  const createCert = async () => {
    if (videoErrors.length) return toast.error(videoErrors.join(" · "), { duration: 10000 });
    if (!cfg.exam_code.trim()) return toast.error("Exam code is required (the vendor's exam ID, e.g. SY0-701)");
    if (factErrors.length) return toast.error("Exam facts: " + factErrors.join(" · "), { duration: 10000 });
    try {
      let eco_domains;
      try { eco_domains = JSON.parse(ecoJson); } catch { return toast.error("ECO JSON is invalid"); }
      const { data } = await api.post("/admin/certs", {
        name: cfg.name, slug: cfg.slug, vendor: cfg.vendor, style_profile: cfg.style_profile,
        version_tag: cfg.version_tag, price_usd: Number(cfg.price_usd),
        channels: { youtube: cfg.youtube, meta: cfg.meta, udemy: cfg.udemy, youtube_channel: cfg.youtube_channel },
        exam_params: { num_paid_exams: +cfg.num_paid_exams, questions_per_exam: +cfg.questions_per_exam, free_count: +cfg.free_count, udemy_exams: +cfg.udemy_exams, udemy_questions: +cfg.udemy_questions, duration_min: +cfg.duration_min, video_questions: +cfg.video_questions, video_episode_size: +cfg.video_episode_size },
        exam_code: cfg.exam_code || null, short_name: cfg.short_name.trim() || null, exam_facts: examFactsPayload(facts),
        eco_domains, eco_extraction_id: eco?.extraction_id, determination_id: verdict?._id,
      });
      setCert(data);
      setStep(3);
      toast.success("Cert created — ECO attached as the first source");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadSource = async () => {
    if (source.text.length < 20) return toast.error("Paste at least 20 characters of source text");
    try {
      const { data } = await api.post(`/admin/certs/${cert._id}/sources`, source);
      setSources([...sources, data]);
      setSource({ filename: "", kind: "study_guide", text: "" });
      setCovKey((k) => k + 1);
      toast.success("Source uploaded");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const [batchApi, setBatchApi] = useState(true);
  const [acceptRisk, setAcceptRisk] = useState(false);
  const generate = async (accept = false) => {
    try {
      await api.post(`/admin/certs/${cert._id}/generate`,
        { use_batch_api: batchApi, ...(accept && { accept_single_source_risk: true }) });
      toast.success("Generation pipeline started");
      navigate("/admin");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "single_source" && !accept) {
        const list = d.objectives.map((o) => `${o.objective_id} (${o.sources} source${o.sources === 1 ? "" : "s"})`).join(", ");
        if (window.confirm(`These objectives lack multi-source support:\n${list}\n\nAdd sources — or press OK to accept single-source risk and start anyway.`)) {
          return generate(true);
        }
        return;
      }
      toast.error(typeof d === "object" && d?.message ? d.message : errMsg(e));
    }
  };

  // §1 v1.12.6.3: test-before-generate on this step, zero navigation away
  const [smoke, setSmoke] = useState(null);
  const [smokeJob, setSmokeJob] = useState(null);
  const smokeRunning = smoke?.status === "running";
  useEffect(() => {
    if (step !== 4 || !cert || !smokeRunning) return undefined;
    const t = setInterval(async () => {
      try {
        const { data: c } = await api.get(`/admin/certs/${cert._id}`);
        setSmoke(c.smoke_result || null);
        if (c.smoke_result?.status === "running" && c.smoke_result.job_id) {
          const { data: j } = await api.get(`/admin/jobs/${c.smoke_result.job_id}`);
          setSmokeJob(j);
        } else {
          setSmokeJob(null);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [step, cert, smokeRunning]);
  const runSmoke = async () => {
    try {
      await api.post(`/admin/certs/${cert._id}/smoke-run`, {});
      setSmoke({ status: "running" });
      toast.success("Test run started — 5 questions, $2 cap, auto-cleaned");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const smokeStages = smokeRunning && smokeJob
    ? Object.fromEntries(smokeJob.stages.map((st, i) => [st,
        i < smokeJob.stage_index ? "pass" : i === smokeJob.stage_index ? "running" : "not_run"]))
    : smoke?.stages || null;

  // §5 v1.12.6.3: rerun without deleting — typed confirmation
  const regenerate = async () => {
    const typed = window.prompt(`Regenerate in place: clears the question bank and derived artifacts, then reruns against the CURRENT ECO, sources and configuration.\nType the cert slug (${cert.slug}) to confirm:`);
    if (typed == null) return;
    try {
      await api.post(`/admin/certs/${cert._id}/regenerate?confirm=${encodeURIComponent(typed)}`, { use_batch_api: batchApi });
      toast.success("Regenerating in place — bank cleared, fresh run started");
      navigate("/admin");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-3xl fade-in">
      <h1 className="text-2xl font-black mb-1">Setup Wizard</h1>
      <div className="flex gap-2 text-xs font-mono mb-6">
        {["1 Cert details", "2 ECO", "3 Sources", "4 Generate"].map((s, i) => (
          <span key={s} className={`px-2 py-1 rounded ${step === i + 1 ? "bg-sky-500 text-white" : "bg-zinc-100 text-zinc-500"}`}>{s}</span>
        ))}
      </div>

      {step === 1 && (
        <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-3" data-testid="wizard-step-1">
          <div>
            <h2 className="text-lg font-bold">Cert details</h2>
            <p className="text-sm text-zinc-600 mt-1">Determine the cert first — go/no-go, then name, code, price and exam configuration. The ECO comes next, sources after that.</p>
          </div>
          <label className="block text-xs font-medium text-zinc-600">Certification name
            <input data-testid="det-cert-name" className={input} placeholder="AWS Solutions Architect Associate" value={det.cert_name} onChange={(e) => setDet({ ...det, cert_name: e.target.value })} /></label>
          <label className="block text-xs font-medium text-zinc-600">Primary keyword
            <input data-testid="det-keyword" className={input} placeholder="aws saa-c03 practice exam" value={det.keyword} onChange={(e) => setDet({ ...det, keyword: e.target.value })} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-medium text-zinc-600">Min monthly searches
              <input data-testid="det-min-searches" type="number" className={input} value={det.min_monthly_searches} onChange={(e) => setDet({ ...det, min_monthly_searches: +e.target.value })} /></label>
            <label className="text-xs font-medium text-zinc-600">Max competition (0–1)
              <input data-testid="det-max-competition" type="number" step="0.05" className={input} value={det.max_competition} onChange={(e) => setDet({ ...det, max_competition: +e.target.value })} /></label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input data-testid="det-override" type="checkbox" checked={det.override} onChange={(e) => setDet({ ...det, override: e.target.checked })} /> Override (force go)</label>
          <button data-testid="det-run-btn" onClick={runDetermination} className="px-4 py-2 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition-colors duration-150">Run determination</button>
          {verdict && (
            <div className={`border rounded-md p-4 text-sm ${verdict.verdict === "go" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="det-verdict">
              <p className="font-bold uppercase">{verdict.verdict}{verdict.overridden && " (overridden)"}</p>
              <p className="font-mono text-xs mt-1">searches {verdict.dataforseo.monthly_searches}/mo · competition {verdict.dataforseo.competition} · top YT video {verdict.youtube.top_video_views.toLocaleString()} views · mode {verdict.dataforseo.mode}</p>
            </div>
          )}
          {verdict?.verdict === "go" && (
            <div className="space-y-3 border-t border-zinc-100 pt-3" data-testid="cfg-block">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-medium text-zinc-600">Certification name
                  <input data-testid="cfg-name" className={input} value={cfg.name} onChange={(e) => setCfg({ ...cfg, name: e.target.value })} /></label>
                <label className="text-xs font-medium text-zinc-600">URL slug (examray.com/…)
                  <input data-testid="cfg-slug" className={input} placeholder="aws-saa" value={cfg.slug} onChange={(e) => setCfg({ ...cfg, slug: e.target.value })} /></label>
                <label className="text-xs font-medium text-zinc-600">Vendor / certifying body
                  <input data-testid="cfg-vendor" className={input} placeholder="CompTIA, PMI…" value={cfg.vendor} onChange={(e) => setCfg({ ...cfg, vendor: e.target.value })} /></label>
                <label className="block text-xs font-medium text-zinc-600">Exam code <span className="text-red-600">*</span>
                  <input data-testid="cfg-exam-code" className={input} placeholder="SY0-701" value={cfg.exam_code} onChange={(e) => setCfg({ ...cfg, exam_code: e.target.value })} /></label>
                <label className="block text-xs font-medium text-zinc-600">Short name (video titles, spec sheet)
                  <input data-testid="cfg-short-name" className={input} placeholder="Security+" value={cfg.short_name} onChange={(e) => setCfg({ ...cfg, short_name: e.target.value })} /></label>
                <label className="text-xs font-medium text-zinc-600">Question style profile
                  <select data-testid="cfg-style" className={input} value={cfg.style_profile} onChange={(e) => setCfg({ ...cfg, style_profile: e.target.value })}>
                    <option value="comptia_direct">CompTIA — direct</option>
                    <option value="pmi_nuanced">PMI — nuanced</option>
                    <option value="isc2_managerial">ISC² — managerial</option>
                    <option value="vendor_cloud">Vendor cloud — pragmatic</option>
                  </select></label>
                <label className="text-xs font-medium text-zinc-600">ECO version tag
                  <input data-testid="cfg-version" className={input} placeholder="v1.0" value={cfg.version_tag} onChange={(e) => setCfg({ ...cfg, version_tag: e.target.value })} /></label>
                <label className="text-xs font-medium text-zinc-600">Price (USD, full access window)
                  <input data-testid="cfg-price" type="number" className={input} value={cfg.price_usd} onChange={(e) => setCfg({ ...cfg, price_usd: e.target.value })} /></label>
              </div>
              <p className="text-xs font-semibold text-zinc-600 pt-2 uppercase tracking-wide">Exam parameters</p>
              <div className="grid grid-cols-3 gap-3">
                {[["num_paid_exams", "Paid exams in collection"], ["questions_per_exam", "Questions per exam"], ["free_count", "Free-50 bank size"], ["udemy_exams", "Udemy preview exams"], ["udemy_questions", "Questions per Udemy exam"], ["duration_min", "Exam duration (minutes)"], ["video_questions", "Video pool (questions for YouTube — never paid items)"], ["video_episode_size", "Questions per video episode"]].map(([k, l]) => (
                <label key={k} className="text-xs font-medium text-zinc-600">{l}{k.startsWith("video_") && <span className="text-red-600"> *</span>}
                  <input data-testid={`cfg-${k}`} type="number" min={k.startsWith("video_") ? 1 : 0} className={`${input} ${k.startsWith("video_") && videoErrors.length ? "border-red-400" : ""}`} value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} /></label>
                ))}
              </div>
              <TitlePreview cfg={cfg} />
              <ExamFactsFields facts={facts} onChange={setFacts} prefix="cfg-facts" errors={factErrors.length && Object.values(facts).some((v) => String(v).trim()) ? factErrors : []} />
              {videoErrors.length > 0 && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="cfg-video-errors">
                  {videoErrors.join(" · ")} — the cert cannot be created until both video fields are set.
                </p>
              )}
              <p className="text-xs font-semibold text-zinc-600 pt-2 uppercase tracking-wide">Channel routing</p>
              <div className="flex gap-4 text-sm items-center flex-wrap">
                {["youtube", "meta", "udemy"].map((ch) => (
                  <label key={ch} className="flex items-center gap-2"><input type="checkbox" data-testid={`cfg-channel-${ch}`} checked={cfg[ch]} onChange={(e) => setCfg({ ...cfg, [ch]: e.target.checked })} /> {ch}</label>
                ))}
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-600">YouTube vertical channel
                  <select data-testid="cfg-youtube-channel" className="border border-zinc-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-sky-400"
                          value={cfg.youtube_channel} onChange={(e) => setCfg({ ...cfg, youtube_channel: e.target.value })}>
                    {ytChannels.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <button data-testid="cfg-continue-btn" onClick={() => { if (!cfg.name || !cfg.slug) return toast.error("Name and slug are required"); if (!cfg.exam_code.trim()) return toast.error("Exam code is required (the vendor's exam ID, e.g. SY0-701)"); if (videoErrors.length) return toast.error(videoErrors.join(" · "), { duration: 10000 }); if (factErrors.length) return toast.error("Exam facts: " + factErrors.join(" · "), { duration: 10000 }); setStep(2); }}
                      disabled={videoErrors.length > 0 || factErrors.length > 0}
                      className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-700 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed">Continue to ECO →</button>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-4" data-testid="wizard-step-2">
          <div>
            <h2 className="text-lg font-bold">ECO — the master filter</h2>
            <p className="text-sm text-zinc-600 mt-1">Everything for <b>{cfg.name}</b> derives from the vendor's exam objectives. Upload the official document (any vendor, any format) or paste a URL — extraction runs in the background; you just confirm the result.</p>
          </div>
          {/* §6: ECO library — instant reuse of a stored extraction */}
          <EcoLibraryOffer vendor={cfg.vendor} examCode={cfg.exam_code || cfg.slug}
                           onUse={(entry) => {
                             setEco({ domains: entry.domains, objective_count: entry.objective_count,
                                      filename: entry.source_filename, chars_received: 0, stored: true });
                             setEcoJson(JSON.stringify(entry.domains, null, 1));
                           }} />
          <EcoUploadPanel onExtracted={(data) => { setEco(data); setEcoJson(JSON.stringify(data.domains, null, 1)); }} />
          {eco && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-emerald-700" data-testid="eco-result-summary">
                ✓ {eco.domains.length} domains, {eco.objective_count} objectives found <span className="text-xs font-normal text-zinc-400">({eco.filename} · {eco.chars_received.toLocaleString()} chars)</span>
              </p>
              <EcoTree domains={eco.domains} />
              <details className="text-sm">
                <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-700">Advanced: edit the extracted objectives JSON</summary>
                <textarea data-testid="eco-json-edit" rows={10} className={`${input} font-mono text-xs mt-2`} value={ecoJson} onChange={(e) => setEcoJson(e.target.value)} />
              </details>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => setStep(1)} className="px-4 py-2 rounded-md border border-zinc-300 text-sm font-semibold">← Back</button>
            {eco && <button data-testid="eco-confirm-btn" onClick={createCert} disabled={videoErrors.length > 0 || factErrors.length > 0}
                    title={videoErrors.length ? videoErrors.join(" · ") : undefined}
                    className="px-4 py-2 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed">
              Confirm objectives & create cert →
            </button>}
          </div>
        </div>
      )}

      {step === 3 && cert && (
        <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-4" data-testid="wizard-step-3">
          <div>
            <h2 className="text-lg font-bold">Add Exam Sources</h2>
            <p className="text-sm text-zinc-600 mt-1">The confirmed ECO is already attached as the first source for <b>{cert.name}</b>. Upload independent study sources — PDF, DOCX, TXT, MD, or CSV, up to 300 MB each. Two-source validation needs at least one source beyond the ECO. The coverage report below maps your sources against the ECO before a generation run is spent.</p>
          </div>
          <label className="block text-xs font-medium text-zinc-600 max-w-xs">Source type
            <select data-testid="src-kind" className={input} value={source.kind} onChange={(e) => setSource({ ...source, kind: e.target.value })}>
              <option value="study_guide">Study guide / book</option>
              <option value="other">Other reference</option>
            </select>
          </label>
          <SourceLibraryPanel certId={cert._id} onAttached={(d) => { setSources((s) => [...s, d]); setCovKey((k) => k + 1); }} />
          <label className="block border-2 border-dashed border-sky-300 bg-sky-50/40 rounded-md p-8 text-center cursor-pointer hover:border-sky-500 transition-colors duration-150">
            <input data-testid="src-file-input" type="file" accept=".pdf,.docx,.txt,.md,.csv" className="hidden"
                   disabled={uploading !== null}
                   onChange={async (e) => {
                     const f = e.target.files?.[0];
                     if (!f) return;
                     setUploading(0);
                     try {
                       const data = await uploadSourceChunked(cert._id, f, source.kind, setUploading);
                       setSources((s) => [...s, data]);
                       setCovKey((k) => k + 1);
                       toast.success(`${data.filename}: ${data.chars_extracted.toLocaleString()} characters extracted`);
                     } catch (err) { toast.error(errMsg(err)); }
                     setUploading(null);
                     e.target.value = "";
                   }} />
            {uploading !== null ? (
              <span className="text-sm font-semibold text-sky-700" data-testid="src-upload-progress">Uploading… {uploading}%</span>
            ) : (
              <>
                <span className="block text-sm font-semibold text-zinc-800">Click to upload a source file</span>
                <span className="block text-xs text-zinc-500 mt-1">PDF · DOCX · TXT · MD · CSV — up to 300 MB (type: {source.kind})</span>
              </>
            )}
          </label>
          <details className="text-sm">
            <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-700">Advanced: paste source text instead</summary>
            <div className="mt-3 space-y-2">
              <label className="block text-xs font-medium text-zinc-600">Filename label
                <input data-testid="src-filename" className={input} placeholder="filename.txt" value={source.filename} onChange={(e) => setSource({ ...source, filename: e.target.value })} /></label>
              <label className="block text-xs font-medium text-zinc-600">Source text
                <textarea data-testid="src-text" rows={5} className={input} placeholder="Paste source text here (min 20 characters)" value={source.text} onChange={(e) => setSource({ ...source, text: e.target.value })} /></label>
              <button data-testid="src-upload-btn" onClick={uploadSource} className="px-4 py-2 rounded-md border border-zinc-300 text-sm font-semibold hover:border-sky-400 transition-colors duration-150">+ Add pasted source</button>
            </div>
          </details>
          <ul className="text-sm font-mono text-zinc-600" data-testid="src-list">
            {sources.map((s) => <li key={s.id}>✓ {s.filename} ({s.kind}{s.chars_extracted ? ` · ${s.chars_extracted.toLocaleString()} chars` : ""})</li>)}
          </ul>
          <CoveragePanel certId={cert._id} refreshKey={covKey} />
          <button data-testid="sources-continue-btn" onClick={() => setStep(4)}
                  className="w-full py-3 rounded-md bg-zinc-900 text-white font-bold hover:bg-zinc-700 transition-colors duration-150">
            Continue → Generate
          </button>
        </div>
      )}

      {step === 4 && cert && (
        <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-4" data-testid="wizard-step-4">
          <div>
            <h2 className="text-lg font-bold">Generate</h2>
            <p className="text-sm text-zinc-600 mt-1">Final check for <b>{cert.name}</b> — the coverage report below is the last look at ECO gaps before the run spends real provider budget.</p>
          </div>
          <CoveragePanel certId={cert._id} refreshKey={covKey} />
          <SetupCheckPanel certId={cert._id} refreshKey={covKey} accept={acceptRisk} setAccept={setAcceptRisk} />
          <label className="flex items-center gap-2 text-sm text-zinc-700" data-testid="wizard-batch-api-toggle">
            <input type="checkbox" checked={batchApi} onChange={(e) => setBatchApi(e.target.checked)} />
            Batch API for generation — 50% generation cost, not latency-sensitive (recommended)
          </label>
          {(smoke || smokeRunning) && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-1.5" data-testid="wizard-smoke-result">
              <p className={`text-xs font-bold ${smoke?.status === "pass" ? "text-emerald-700" : smoke?.status === "fail" ? "text-red-700" : "text-zinc-600"}`}>
                Test run: {smokeRunning ? "running…" : smoke?.status?.toUpperCase()}
                {smoke?.spend_usd != null && ` · $${smoke.spend_usd.toFixed(2)}`}
              </p>
              {smokeStages && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(smokeStages).map(([st, res]) => (
                    <span key={st} data-testid={`wizard-smoke-stage-${st}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                            res === "pass" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : res === "fail" ? "bg-red-50 text-red-700 border-red-200"
                              : res === "running" ? "bg-sky-50 text-sky-700 border-sky-200"
                              : "bg-zinc-50 text-zinc-400 border-zinc-200"}`}>
                      {st} {res === "pass" ? "✓" : res === "fail" ? "✗" : res === "running" ? "…" : "—"}
                    </span>
                  ))}
                </div>
              )}
              {smoke?.error && <p className="text-[11px] text-red-600">{smoke.error}</p>}
              {smoke?.verdicts?.length > 0 && (
                <div className="space-y-0.5" data-testid="wizard-smoke-verdicts">
                  {smoke.verdicts.map((v, i) => (
                    <p key={i} className="text-[11px] text-zinc-500">
                      <span className={`font-semibold ${v.verdict === "pass" ? "text-emerald-700" : "text-red-700"}`}>{v.verdict}</span>
                      {" — "}{v.reason} · “{v.question}”
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => setStep(3)} className="px-4 py-2 rounded-md border border-zinc-300 text-sm font-semibold">← Back to sources</button>
            <button data-testid="wizard-smoke-run-btn" onClick={runSmoke} disabled={smokeRunning}
                    className="px-4 py-2 rounded-md border border-sky-300 text-sky-700 text-sm font-bold hover:bg-sky-50 transition-colors duration-150 disabled:opacity-50">
              {smokeRunning ? "Test running…" : "Test run ($2 cap)"}
            </button>
            <button data-testid="wizard-regenerate-btn" onClick={regenerate}
                    className="px-4 py-2 rounded-md border border-amber-300 text-amber-700 text-sm font-bold hover:bg-amber-50 transition-colors duration-150">
              Regenerate
            </button>
            <button data-testid="wizard-generate-btn" onClick={() => generate(acceptRisk)}
                    className="flex-1 py-3 rounded-md bg-sky-500 text-white font-bold hover:bg-sky-600 transition-colors duration-150">
              Generate →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
